import { BadgeActionCard, LoadingActionCard } from '@account/components';
import { EarnedBadge } from '@account/types';
import { PaginationReturn } from '@common/types/service';
import { CenteredEmptyState } from '@components/display/emptyStates';
import { useDashboardLayoutScrollElement } from '@components/layout';
import { isInfiniteLoadingItem, useInfiniteLazyLoader } from '@hooks/useInfiniteLazyLoader';
import { Grid2 } from '@mui/material';
import { useLazyGetAccountBadgesQuery } from '@services/accounts';
import { handleError } from '@utils/common';
import { useParams } from 'react-router-dom';

export const MemberEarnedBadges = () => {
    const scrollElement = useDashboardLayoutScrollElement();
    const { id: user_id } = useParams();

    const [getAccountBadges, { isError: isErrorFetchingBadges }] = useLazyGetAccountBadgesQuery();

    const loadBadgesPage = async ({ page, pageSize }: { page: number; pageSize: number }) => {
        const response = await getAccountBadges(
            {
                page,
                page_size: pageSize,
                user_id: Number(user_id),
            },
            true
        ).unwrap();

        return response as PaginationReturn<EarnedBadge>;
    };

    const { items, sentinelRef, hasRealItems, isLoadingMore } = useInfiniteLazyLoader<EarnedBadge>({
        pageSize: 8,
        scrollElement,
        contextKey: `earned-badges:8`,
        loadPage: loadBadgesPage,
        getItemKey: (badge) => badge.id,
        onError: (error) => {
            console.error('Error loading badges:', error);
            handleError('Error loading badges', error);
        },
    });

    return (
        <Grid2 container py="24px" columnSpacing="48px" rowSpacing="24px">
            {hasRealItems || isLoadingMore ? (
                items.map((badge, index) =>
                    isInfiniteLoadingItem(badge) ? (
                        <Grid2 key={`skeleton-${index}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                            <LoadingActionCard />
                        </Grid2>
                    ) : (
                        <Grid2 key={`badge-${badge.id}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                            <BadgeActionCard badge={badge} className="!min-w-0 !w-auto" />
                        </Grid2>
                    )
                )
            ) : isErrorFetchingBadges ? (
                <CenteredEmptyState message="An error occurred while fetching this member's badges." />
            ) : (
                <CenteredEmptyState message="This member hasn't earned any badges yet." />
            )}
            <div ref={sentinelRef} className="basis-full h-px" />
        </Grid2>
    );
};
